// src/components/Common/ErrorBoundary.js
import React from 'react';
import { supabase } from '../../services/supabase';
import './ErrorBoundary.css';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorCount: 0,
      isOnline: navigator.onLine
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    const errorCount = this.state.errorCount + 1;
    
    // Log error details
    console.error('Error caught by boundary:', error, errorInfo);
    
    // Update state with error details
    this.setState({
      error,
      errorInfo,
      errorCount
    });

    // Log to error tracking service (in production)
    this.logErrorToService(error, errorInfo);
    
    // Store error in localStorage for debugging
    this.storeErrorLocally(error, errorInfo);
  }

  componentDidMount() {
    // Monitor online status
    window.addEventListener('online', this.handleOnline);
    window.addEventListener('offline', this.handleOffline);
    
    // Clear old errors on mount
    this.clearOldErrors();
  }

  componentWillUnmount() {
    window.removeEventListener('online', this.handleOnline);
    window.removeEventListener('offline', this.handleOffline);
  }

  handleOnline = () => {
    this.setState({ isOnline: true });
  };

  handleOffline = () => {
    this.setState({ isOnline: false });
  };

  logErrorToService = async (error, errorInfo) => {
    try {
      // Only log if online and in production
      if (navigator.onLine && process.env.NODE_ENV === 'production') {
        // Get current user info
        const { data: { user } } = await supabase.auth.getUser();
        
        const errorData = {
          message: error.toString(),
          stack: error.stack,
          componentStack: errorInfo.componentStack,
          userAgent: navigator.userAgent,
          timestamp: new Date().toISOString(),
          userId: user?.id || 'anonymous',
          url: window.location.href,
          isOnline: navigator.onLine
        };

        // You could send this to a logging service or Supabase
        console.error('Error logged:', errorData);
      }
    } catch (logError) {
      console.error('Failed to log error:', logError);
    }
  };

  storeErrorLocally = (error, errorInfo) => {
    try {
      const errorLog = {
        message: error.toString(),
        stack: error.stack,
        componentStack: errorInfo.componentStack,
        timestamp: new Date().toISOString(),
        url: window.location.href
      };

      // Get existing errors
      const existingErrors = JSON.parse(
        localStorage.getItem('iyewo-error-log') || '[]'
      );

      // Keep only last 10 errors
      const updatedErrors = [errorLog, ...existingErrors].slice(0, 10);
      
      localStorage.setItem('iyewo-error-log', JSON.stringify(updatedErrors));
    } catch (e) {
      console.error('Failed to store error locally:', e);
    }
  };

  clearOldErrors = () => {
    try {
      const errors = JSON.parse(
        localStorage.getItem('iyewo-error-log') || '[]'
      );
      
      // Keep errors from last 7 days
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      
      const recentErrors = errors.filter(error => 
        new Date(error.timestamp) > weekAgo
      );
      
      localStorage.setItem('iyewo-error-log', JSON.stringify(recentErrors));
    } catch (e) {
      console.error('Failed to clear old errors:', e);
    }
  };

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
  };

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      const { error, errorInfo, errorCount, isOnline } = this.state;
      const isDevelopment = process.env.NODE_ENV === 'development';
      
      // Check if it's a chunk loading error
      const isChunkError = error?.message?.includes('Loading chunk') || 
                          error?.message?.includes('Loading CSS chunk');
      
      // Check if it's a network error
      const isNetworkError = error?.message?.includes('fetch') ||
                            error?.message?.includes('NetworkError') ||
                            !isOnline;

      return (
        <div className="error-boundary">
          <div className="error-container">
            <div className="error-icon">
              {isNetworkError ? '📡' : '⚠️'}
            </div>
            
            <h1>
              {isNetworkError 
                ? 'Connection Problem' 
                : 'Something went wrong'
              }
            </h1>
            
            <p className="error-description">
              {isChunkError
                ? 'The application needs to be refreshed to load the latest version.'
                : isNetworkError
                ? 'Please check your internet connection and try again.'
                : 'We encountered an unexpected error. The issue has been logged.'}
            </p>

            {!isOnline && (
              <div className="offline-notice">
                <span className="offline-icon">🔌</span>
                You are currently offline
              </div>
            )}

            <div className="error-actions">
              {isChunkError ? (
                <button onClick={this.handleReload} className="primary-btn">
                  Refresh Application
                </button>
              ) : (
                <>
                  <button onClick={this.handleReset} className="primary-btn">
                    Try Again
                  </button>
                  <button onClick={this.handleReload} className="secondary-btn">
                    Reload Page
                  </button>
                  <button onClick={this.handleGoHome} className="secondary-btn">
                    Go to Home
                  </button>
                </>
              )}
            </div>

            {errorCount > 2 && (
              <div className="error-warning">
                <p>Multiple errors detected. If problems persist:</p>
                <ol>
                  <li>Clear your browser cache</li>
                  <li>Check your internet connection</li>
                  <li>Try using a different browser</li>
                  <li>Contact support if the issue continues</li>
                </ol>
              </div>
            )}

            {isDevelopment && error && (
              <details className="error-details">
                <summary>Error Details (Development Only)</summary>
                <div className="error-stack">
                  <h3>Error Message:</h3>
                  <pre>{error.toString()}</pre>
                  
                  <h3>Stack Trace:</h3>
                  <pre>{error.stack}</pre>
                  
                  <h3>Component Stack:</h3>
                  <pre>{errorInfo?.componentStack}</pre>
                </div>
              </details>
            )}

            <div className="error-footer">
              <p>Error ID: {Date.now()}</p>
              <p>If you continue to experience issues, please contact support.</p>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Role-specific error boundary
export class RoleErrorBoundary extends ErrorBoundary {
  render() {
    if (this.state.hasError) {
      const role = this.props.role;
      
      return (
        <div className="error-boundary role-specific">
          <div className="error-container">
            <h1>Dashboard Error</h1>
            <p>
              There was a problem loading the {role} dashboard.
            </p>
            <div className="error-actions">
              <button onClick={this.handleReset} className="primary-btn">
                Retry
              </button>
              <button onClick={() => window.location.href = '/login'} className="secondary-btn">
                Return to Login
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Async error boundary for handling promise rejections
export const AsyncErrorBoundary = ({ children }) => {
  React.useEffect(() => {
    const handleUnhandledRejection = (event) => {
      console.error('Unhandled promise rejection:', event.reason);
      
      // Store in local storage for debugging
      try {
        const rejections = JSON.parse(
          localStorage.getItem('iyewo-unhandled-rejections') || '[]'
        );
        rejections.push({
          reason: event.reason?.toString(),
          timestamp: new Date().toISOString(),
          url: window.location.href
        });
        localStorage.setItem(
          'iyewo-unhandled-rejections', 
          JSON.stringify(rejections.slice(-10))
        );
      } catch (e) {
        console.error('Failed to log rejection:', e);
      }
    };

    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    
    return () => {
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  return <>{children}</>;
};

export default ErrorBoundary;